// ─── Cluster G — Toggles (structural-mods + user-context + audience-level) ───
// Per docs/design/app-js-split-plan-260522.md §3 Cluster G / Module 8.
//
// First .ts module in web/static/spa/ (per the app.js split Phase 8, 2026-05-22).
// Compiled to ./toggles.js by tsc -p tsconfig.emit.json; the browser imports
// the .js file via the hard-coded `./toggles.js` path. The .ts source is
// canonical; the .js sibling is a derived artifact (gitignored).
//
// Exports (public API surface):
//   - showStructuralModToggleForUser(user) — initialize SM rail + bind handlers
//   - hideStructuralModToggle()            — no-op back-compat shim
//   - showMathFormatToggleForUser(user)    — no-op back-compat shim
//   - hideMathFormatToggle()               — no-op back-compat shim
//   - smComputeUploadDiff()                — diff vs user's stored default
//   - userContextAltTextForUpload()        — current text-area value (or null)
//   - audienceLevelForUpload()             — current dropdown enum (or null)
//   - initAudienceLevel()                  — wire dropdown + localStorage
//   - initUserContextAltText()             — wire toggle/textarea + localStorage
//
// Imports from sibling split modules:
//   globals-boot.mjs    → authFetch, API
//   auth-and-billing.js → getAuthProfile (Phase 9 retired the
//                          window._adaGetAuthProfile bridge)
//
// Dual-state note: while production still runs app.js (until Phase 11 swaps
// the HTML entry), app.js's `_authProfileData` is the authoritative auth-
// profile state. The Phase-9 module imported here is a parallel typed
// extract — its singleton's profile is NOT populated by production callers
// today. The previous design used `window._adaGetAuthProfile` to bridge into
// app.js's live state; with Phase 9 landed, the canonical reader is
// `import { getAuthProfile } from './auth-and-billing.js'`. Once Phase 11
// swaps the entry point, the import becomes the live read.
//
// Globals (read-only at call time):
//   window.adatool.log — logging
//   document, localStorage — DOM + per-tab persistence
//
// See:
//   docs/design/structural-mods-toggle-redesign-260507.md §11 (SM design)
//   docs/design/user-context-altext-260510.md (user-context textarea)
//   docs/design/audience-level-slider-260510.md (audience dropdown)
//   docs/design/better-math-accessibility-260513.md §3 (math-format no-op)
import { authFetch, API } from './globals-boot.mjs';
import { getAuthProfile } from './auth-and-billing.js';
import { SM_LEAVES, SM_SPEC_DEFAULT, SM_LEAF_TO_ID, SM_LS_KEY_PREFIX, USER_CONTEXT_ALT_TEXT_MAX_CHARS, USER_CONTEXT_LS_TEXT, USER_CONTEXT_LS_ENABLED, AUDIENCE_LEVEL_LABELS, AUDIENCE_LEVEL_LS_INDEX, smInitialLeavesForUser as _smInitialLeavesForUserPure, smInitialLeavesForAnon as _smInitialLeavesForAnonPure, smComputeDiff, smAudienceLevelFromIndex, smClampAltText, } from './toggles-logic.js';
// ─── Read the live auth profile from the sibling Phase 9 module ──────────────
// Returns the AuthProfile object (with optional `user.structural_mod_prefs`)
// so smPersistOnLeafChange can mutate user.structural_mod_prefs in place on
// PUT-success. The structural-mod prefs are an opaque-to-toggles cache —
// only `.user.structural_mod_prefs` is read/written here.
function _getAuthProfile() {
    return getAuthProfile();
}
// ─── Structural-mods toggle UI (flat compact grid, 260902 redesign) ──────────
//
// Wire format = leaf values only (`SM_LEAVES`); each control is an INDEPENDENT
// knob. The 260902 compact redesign removed the master toggle + per-format
// sub-master rollup (SM_TREE) — the hierarchy had become near-vestigial (a
// single-leaf Excel sub-master) and no longer earned its complexity, so the
// panel is now a flat grid with no chaining. Per-job uploads send a *diff*
// against the user's stored default (`sm_prefs_diff` form field) — the server
// resolves the diff against the stored prefs (or the spec-default for anon
// users) before snapshotting into jobs.options.sm_prefs.
//
// Persistence:
//   - Authed users: `users.structural_mod_prefs` JSONB column. Server
//     authoritative; SPA caches via /api/auth/me on page load.
//   - Anonymous users: per-leaf localStorage keys (`ada_tool:sm:<leaf>`)
//     overlay onto the spec-default constant.
//
// Constants + pure computation functions are in toggles-logic.ts; imported above.
// Module aliases (keep _SM_ prefix for internal use in this file).
const _SM_LEAVES = SM_LEAVES;
const _SM_SPEC_DEFAULT = SM_SPEC_DEFAULT;
const _SM_LEAF_TO_ID = SM_LEAF_TO_ID;
// In-memory cache of the user's stored default (or spec-default for anon).
// Diff at upload time is computed against this value — empty diff means
// "user is on default; server uses stored prefs as-is".
let _smStoredDefault = { ..._SM_SPEC_DEFAULT };
function _smReadCurrentLeaves() {
    const out = {};
    for (const leaf of _SM_LEAVES) {
        const el = document.getElementById(_SM_LEAF_TO_ID[leaf]); // WHY: leaf ∈ _SM_LEAVES ⊆ keys(_SM_LEAF_TO_ID)  // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        out[leaf] = !!(el?.checked);
    }
    return out;
}
function _smApplyLeavesToUI(leaves) {
    // Flat model (260902): set each leaf checkbox directly; no master/sub-master
    // rollup. Two url_text leaves share the #sm-url-text control — both write
    // it (same default), which is harmless.
    for (const leaf of _SM_LEAVES) {
        const el = document.getElementById(_SM_LEAF_TO_ID[leaf]); // WHY: leaf ∈ _SM_LEAVES ⊆ keys(_SM_LEAF_TO_ID)  // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (!el)
            continue;
        el.checked = !!leaves[leaf];
    }
}
function _smPersistOnLeafChange(leaf, value, user) {
    if (user) {
        // Authed: write the full leaf object to /api/account/preferences.
        // The server replaces the stored default wholesale (§11.5). The
        // local _smStoredDefault is updated optimistically; on PUT failure
        // we revert and surface an error.
        const updated = { ..._smStoredDefault, [leaf]: value };
        const previous = _smStoredDefault;
        _smStoredDefault = updated;
        /* eslint-disable promise/prefer-await-to-then -- fire-and-forget optimistic PUT; containing function is sync (toggle callers expect void return) */
        authFetch(`${API}/api/account/preferences`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ structural_mod_prefs: updated }),
        }).then((resp) => {
            if (!resp.ok) {
                _smStoredDefault = previous;
                window.adatool.log?.warn('sm-toggle PUT failed:', resp.status);
            }
            else {
                // Mirror the optimistic update onto the auth-profile cache so
                // other readers of _authProfileData see the new prefs.
                const profile = _getAuthProfile();
                if (profile?.user) {
                    profile.user.structural_mod_prefs = updated;
                }
            }
        }).catch((err) => {
            _smStoredDefault = previous;
            window.adatool.log?.warn('sm-toggle PUT error:', err);
        });
        /* eslint-enable promise/prefer-await-to-then */
    }
    else {
        // Anon: per-leaf localStorage key (prefix from SM_LS_KEY_PREFIX in toggles-logic.ts).
        try {
            localStorage.setItem(`${SM_LS_KEY_PREFIX}${leaf}`, value ? '1' : '0');
        } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        catch (e) { /* best-effort; Safari private-mode may throw */
            void e;
        }
    }
}
function _smInitialLeavesForAnon() {
    // Delegates to pure smInitialLeavesForAnon (toggles-logic.ts) with real localStorage.
    return _smInitialLeavesForAnonPure((key) => {
        try {
            return localStorage.getItem(key);
        } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        catch (e) {
            void e;
            return null;
        }
    });
}
function _smInitialLeavesForUser(user) {
    return _smInitialLeavesForUserPure(user);
}
// ─── Public API ──────────────────────────────────────────────────────────────
export function showStructuralModToggleForUser(user) {
    // Initialize each leaf checkbox from the user's stored default (or anon
    // localStorage), wire a per-leaf change handler that persists the single
    // leaf, and capture the resolved default into _smStoredDefault so
    // upload-time diff computation can compare against it. Idempotent via
    // _adaSmBound. Flat model (260902 redesign): no master, no sub-master, no
    // rollup — every knob is independent, so toggling one never sweeps another
    // (this also closes the old master-sweep speaker-notes consent bug).
    const rail = document.getElementById('sm-options-rail'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!rail)
        return;
    const initial = user ? _smInitialLeavesForUser(user) : _smInitialLeavesForAnon();
    _smStoredDefault = user ? { ..._SM_SPEC_DEFAULT, ...initial } : { ..._SM_SPEC_DEFAULT };
    _smApplyLeavesToUI(initial);
    if (rail._adaSmBound)
        return; // eslint-disable-line @typescript-eslint/no-unsafe-member-access -- WHY: _adaSmBound is a per-element idempotency flag not in HTMLElement interface
    rail._adaSmBound = true; // eslint-disable-line @typescript-eslint/no-unsafe-member-access -- WHY: idempotency flag on element
    // Each leaf change persists that single leaf (no rollup — flat grid). The
    // two url_text leaves share the #sm-url-text control, so both bind a listener
    // to it and both persist on change — the intended dual-map behaviour.
    for (const leaf of _SM_LEAVES) {
        const id = _SM_LEAF_TO_ID[leaf]; // WHY: leaf ∈ _SM_LEAVES ⊆ keys(_SM_LEAF_TO_ID)
        const el = document.getElementById(id); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (!el)
            continue;
        el.addEventListener('change', () => {
            const u = _getAuthProfile()?.user ?? null;
            _smPersistOnLeafChange(leaf, el.checked, u);
        });
    }
}
export function smComputeUploadDiff() {
    // Diff = { leaf: current[leaf] for each leaf where current[leaf]
    // differs from the user's stored default }. Server resolves empty
    // diff to "use stored default unchanged"; full diff means all
    // overrides. See §11.5.
    // Delegates to pure smComputeDiff (toggles-logic.ts).
    const current = _smReadCurrentLeaves();
    return smComputeDiff(current, _smStoredDefault);
}
// ─── User-supplied alt-text context (260510) ─────────────────────────────────
//
// Single text-box that applies to ALL files in the upload batch. Toggle +
// textarea text persist in localStorage so the user's context survives page
// reload during a session. 2000-character cap matches the server-side gate;
// the client-side counter shows live usage.
// See docs/design/user-context-altext-260510.md.
//
// USER_CONTEXT_ALT_TEXT_MAX_CHARS, USER_CONTEXT_LS_TEXT, USER_CONTEXT_LS_ENABLED
// are imported from toggles-logic.ts.
// Read current user-context value to send with the upload. Returns:
//   - the textarea content (trimmed) when the toggle is ON and content
//     is non-empty
//   - null when the toggle is OFF or the textarea is empty/whitespace
// The caller appends this to FormData only when non-null.
export function userContextAltTextForUpload() {
    const enabled = document.getElementById('user-context-alt-text-enabled'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!enabled?.checked)
        return null;
    const ta = document.getElementById('user-context-alt-text'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!ta)
        return null;
    // Delegates to pure smClampAltText (toggles-logic.ts) for trim + cap.
    return smClampAltText(ta.value);
}
// ─── Audience-level dropdown (260510) ────────────────────────────────────────
// 9 positions: Off (0) + 8 education levels (K-3 .. Professional).
// Position-index → wire-format enum mapping. Position 0 (Off) means "skip
// the prompt injection entirely" and the SPA omits the field from FormData.
// See docs/design/audience-level-slider-260510.md.
//
// AUDIENCE_LEVEL_BY_INDEX, AUDIENCE_LEVEL_LABELS, AUDIENCE_LEVEL_LS_INDEX
// are imported from toggles-logic.ts.
// Read current audience-level wire-format value for upload. Returns the
// enum string (e.g. "Graduate") when set, or null when Off (position 0).
// The caller appends to FormData only when non-null.
export function audienceLevelForUpload() {
    const sel = document.getElementById('audience-level'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!sel)
        return null;
    const idx = Number.parseInt(sel.value, 10);
    // Delegates to pure smAudienceLevelFromIndex (toggles-logic.ts).
    return smAudienceLevelFromIndex(idx);
}
// Wire dropdown: localStorage round-trip. Idempotent — safe to call from
// both DOMContentLoaded and post-auth init paths.
export function initAudienceLevel() {
    const sel = document.getElementById('audience-level'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!sel)
        return;
    if (sel.dataset.audWired === '1')
        return;
    sel.dataset.audWired = '1';
    // Restore persisted state from localStorage.
    try {
        const saved = localStorage.getItem(AUDIENCE_LEVEL_LS_INDEX); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (saved != null) {
            const idx = Number.parseInt(saved, 10);
            if (Number.isFinite(idx) && idx >= 0 && idx < AUDIENCE_LEVEL_LABELS.length)
                sel.value = String(idx);
        }
    }
    catch (e) { /* localStorage may be disabled in private mode */
        void e;
    }
    sel.addEventListener('change', () => {
        try {
            localStorage.setItem(AUDIENCE_LEVEL_LS_INDEX, sel.value);
        } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        catch (e) { /* localStorage may be disabled */
            void e;
        }
    });
}
// Wire toggle + textarea: localStorage round-trip, live char counter,
// reveal/collapse on toggle. Idempotent — safe to call from both
// DOMContentLoaded and post-auth init paths.
export function initUserContextAltText() {
    const toggle = document.getElementById('user-context-alt-text-enabled'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const region = document.getElementById('user-context-alt-text-region'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const ta = document.getElementById('user-context-alt-text'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const counter = document.getElementById('user-context-alt-text-counter'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!toggle || !region || !ta || !counter)
        return;
    if (toggle.dataset.uctxWired === '1')
        return;
    toggle.dataset.uctxWired = '1';
    // Restore persisted state from localStorage.
    try {
        const savedText = localStorage.getItem(USER_CONTEXT_LS_TEXT); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (savedText != null)
            ta.value = savedText.slice(0, USER_CONTEXT_ALT_TEXT_MAX_CHARS);
        const savedEnabled = localStorage.getItem(USER_CONTEXT_LS_ENABLED) === '1'; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        toggle.checked = savedEnabled;
    }
    catch (e) { /* localStorage may be disabled in private mode */
        void e;
    }
    const updateCounter = () => {
        const len = (ta.value || '').length;
        counter.textContent = `${len} / ${USER_CONTEXT_ALT_TEXT_MAX_CHARS}`;
    };
    const applyVisibility = () => {
        region.style.display = toggle.checked ? '' : 'none';
    };
    applyVisibility();
    updateCounter();
    toggle.addEventListener('change', () => {
        applyVisibility();
        try {
            localStorage.setItem(USER_CONTEXT_LS_ENABLED, toggle.checked ? '1' : '0');
        } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        catch (e) { /* localStorage may be disabled */
            void e;
        }
    });
    ta.addEventListener('input', () => {
        updateCounter();
        try {
            localStorage.setItem(USER_CONTEXT_LS_TEXT, ta.value || '');
        } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        catch (e) { /* localStorage may be disabled */
            void e;
        }
    });
}
// ─── Embed-fonts-with-substitution knob (M1 — font-corpus Phase 2) ───────────
//
// A single per-job boolean toggle: the user-visible generic-substitution knob
// (phase-1b-review §3.3). Default UNCHECKED (OFF) — matches the C#
// AltTextGenerationConfig.EmbedFontsWithSubstitution CLR default. When on, an
// unmapped non-embedded font with no metric-compatible substitute is embedded
// with a close FOSS generic (layout may shift slightly). The value travels as
// a per-job form field → job option → the CLI --config's
// embedFontsWithSubstitution. Label is fixed verbatim per the user ruling.
// See docs/epics/active/font-corpus-inventory-and-expansion-260901/.
// The verbatim user-facing label (user ruling, G-1). Exported so the SPA
// template and any test can assert the exact wording — it must not drift.
export const EMBED_FONTS_SUBSTITUTION_LABEL = 'Embed fonts, possibly resulting in a slight change of appearance';
const EMBED_FONTS_SUBSTITUTION_LS = 'ada_tool:embed_fonts_substitution';
// Read the current knob value for upload. Returns true only when the checkbox
// is present AND checked; false otherwise (default-OFF). The caller appends
// the field to FormData only when true (mirrors audience_level's null-skip).
export function embedFontsWithSubstitutionForUpload() {
    const el = document.getElementById('embed-fonts-with-substitution'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    return !!el?.checked;
}
// Wire the checkbox: localStorage round-trip so the choice survives a reload
// during a session. Idempotent — safe to call from DOMContentLoaded and
// post-auth init paths. Default OFF when no persisted value exists.
export function initEmbedFontsWithSubstitution() {
    const toggle = document.getElementById('embed-fonts-with-substitution'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!toggle)
        return;
    if (toggle.dataset.efsWired === '1')
        return;
    toggle.dataset.efsWired = '1';
    // Restore persisted state (default OFF — never auto-enable).
    try {
        toggle.checked = localStorage.getItem(EMBED_FONTS_SUBSTITUTION_LS) === '1'; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }
    catch (e) { /* localStorage may be disabled in private mode */
        void e;
    }
    toggle.addEventListener('change', () => {
        try {
            localStorage.setItem(EMBED_FONTS_SUBSTITUTION_LS, toggle.checked ? '1' : '0');
        } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        catch (e) { /* localStorage may be disabled */
            void e;
        }
    });
}
// ─── Back-compat no-op shims ─────────────────────────────────────────────────
export function hideStructuralModToggle() {
    // No-op: SM rail is always visible regardless of auth state.
    // Retained for backward-compat call sites.
}
export function showMathFormatToggleForUser(_user) {
    // No-op: the per-upload math-format toggle was removed in the Tier 1
    // simplification (alt-text is always spoken prose). Retained as a
    // back-compat shim for call sites that may still reference it.
    // See docs/design/better-math-accessibility-260513.md §3.
}
export function hideMathFormatToggle() { }
